import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUserContext } from "../contexts";
import Navbar from "./Nav";
import PageInfo from "./PageInfo/PageInfo";
import { useQueryLeaderboard } from "./UserStats/useQueryLeaderboard";
import { useQueryRank } from "./UserStats/useQueryRank";
import { SPORTS } from "./UserStats/userStats.constants";
import styles from "./leaderboard.module.scss";
import Lottie from "lottie-react";
import basketballAnimation from "../assets/lottie/basketball.json";

const columns = [
  { width: "10%", label: "Rank", dataKey: "rank" },
  { width: "25%", label: "Username", dataKey: "username" },
  { width: "15%", label: "Wins", dataKey: "wins", numeric: true },
  { width: "15%", label: "Losses", dataKey: "losses", numeric: true },
  { width: "15%", label: "Draws", dataKey: "draws", numeric: true },
  { width: "20%", label: "Rating", dataKey: "elo", numeric: true },
];

const Leaderboard = () => {
  const { username } = useUserContext();
  const [selectedSport, setSelectedSport] = useState(SPORTS[0]);

  const {
    loading,
    sportId,
    leaderboard,
    onChangeSport,
    onChangePage,
    disabledMore,
    isLoadingMore,
  } = useQueryLeaderboard();
  const { rank } = useQueryRank();

  const handleChangeSport = (newSportId) => {
    const newObjSport = SPORTS.find((sport) => sport.id === newSportId);
    setSelectedSport(newObjSport || SPORTS[0]);
    onChangeSport(newSportId);
  };

  const handleChangePage = async () => {
    await onChangePage(sportId);
  };

  const isUserLeaderboard = useMemo(
    () => Boolean(leaderboard?.find((item) => item.username === username)),
    [leaderboard, username]
  );

  const location = useLocation();

  if (loading && (!leaderboard || leaderboard.length === 0)) {
    return (
      <div className={styles.loader}>
        <Lottie
          animationData={basketballAnimation}
          style={{ width: 200, height: 200, marginBottom: 40 }}
        />
        <div>Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <PageInfo
        title="Leaderboard - Crossover Grid Rankings"
        metaTitle="Leaderboard - Crossover Grid Rankings"
        metaDescription="View the Crossover Grid leaderboard! See top players ranked by wins, losses, draws, and ELO rating across all sports."
        metaUrl={location.pathname}
        keywords={["leaderboard", "crossover grid rankings", "top players", "sports leaderboard", "game rankings", "elo rating"]}
        twitterCreator="@CrossoverGrid"
      />
      <Navbar />

      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            🏆 {selectedSport?.label} Leaderboard
          </h1>
          <p className={styles.subtitle}>
            Top players ranked by performance and rating
          </p>
        </div>

        <div className={styles.sportTabs}>
          {SPORTS.map((sport) => (
            <button
              key={sport.id}
              className={`${styles.sportTab} ${
                selectedSport.id === sport.id ? styles.active : ""
              }`}
              onClick={() => handleChangeSport(sport.id)}
            >
              {sport.label}
            </button>
          ))}
        </div>

        <div className={styles.tableWrapper}>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.headerRow}>
                  {columns.map((column) => (
                    <th
                      key={column.dataKey}
                      className={styles.headerCell}
                      style={{ width: column.width }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leaderboard?.map((row, rowIndex) => {
                  const isUserRow = row.username === username;
                  const isPodium = rowIndex < 3;

                  return (
                    <tr
                      key={row.username}
                      className={`${styles.row} ${
                        isUserRow ? styles.userRow : ""
                      } ${isPodium ? styles.podiumRow : ""}`}
                    >
                      <td className={styles.cell}>
                        {isPodium && (
                          <span className={styles.medal}>
                            {rowIndex === 0 && "🥇"}
                            {rowIndex === 1 && "🥈"}
                            {rowIndex === 2 && "🥉"}
                          </span>
                        )}
                        <span className={styles.rank}>#{row.rank}</span>
                      </td>
                      <td className={`${styles.cell} ${styles.usernameCell}`}>
                        {row.username}
                        {isUserRow && (
                          <span className={styles.youBadge}>YOU</span>
                        )}
                      </td>
                      <td className={styles.cell}>{row.wins}</td>
                      <td className={styles.cell}>{row.losses}</td>
                      <td className={styles.cell}>{row.draws}</td>
                      <td className={`${styles.cell} ${styles.ratingCell}`}>
                        <span className={styles.rating}>{row.elo}</span>
                      </td>
                    </tr>
                  );
                })}
                {rank && !isUserLeaderboard && (
                  <tr className={`${styles.row} ${styles.userRow}`}>
                    <td className={styles.cell}>
                      <span className={styles.rank}>#{rank.rank}</span>
                    </td>
                    <td className={`${styles.cell} ${styles.usernameCell}`}>
                      {rank.username}
                      <span className={styles.youBadge}>YOU</span>
                    </td>
                    <td className={styles.cell}>{rank.wins}</td>
                    <td className={styles.cell}>{rank.losses}</td>
                    <td className={styles.cell}>{rank.draws}</td>
                    <td className={`${styles.cell} ${styles.ratingCell}`}>
                      <span className={styles.rating}>{rank.elo}</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && !disabledMore && (
          <div className={styles.loadMoreContainer}>
            <button
              onClick={handleChangePage}
              className={styles.loadMoreButton}
              disabled={disabledMore || loading || isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <span className={styles.spinner}></span>
                  Loading...
                </>
              ) : (
                "Load More"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
